/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Select } from 'antd'
import type { CustomCellEditorProps } from 'ag-grid-react'
import { useState } from 'react'

import colors from '@sportninja/common/constants/appColors'
import type { TeamRef } from '@sportninja/common/features/game/gameListView/types'

import { font } from 'src/components/css'

import type { GameGridRow } from '../utils/gameGridRows'
import { gameViewsLabel } from '../utils/i18n'

export const TEAM_FIELD_ID = 'team-editor-select'

export interface TeamCellEditorParams {
  teamsById: Map<string, TeamRef>
  teamsLoading?: boolean
}

/**
 * The team on ONE side of a game.
 *
 * A custom editor rather than `agSelectCellEditor` for one reason: a
 * competition routinely holds fifty teams and the built-in select has no
 * typeahead. `agRichSelectCellEditor` is the one that does, and it is
 * Enterprise — which we do not have a licence for.
 *
 * The value in and out is the team's UID. Option columns in this grid store ids
 * and render labels beside them because team names genuinely repeat across
 * conferences, so resolving a name back to an id would be a coin flip.
 *
 * A pick commits and closes; Escape leaves the row alone. There is no Apply
 * button — this is one value, unlike `VenueCellEditor`, which needs one because
 * a location and a surface only make sense committed together.
 */
const TeamCellEditor = ({
  value,
  onValueChange,
  stopEditing,
  teamsById,
  teamsLoading = false,
}: CustomCellEditorProps<GameGridRow> & TeamCellEditorParams) => {
  const current = typeof value === 'string' && value !== '' ? value : null

  /*
   * The popup portals into THIS panel's own node, so a pick lands inside the
   * editor and `stopEditingWhenCellsLoseFocus` does not read it as a
   * click-outside and tear the editor down mid-pick.
   *
   * Held as STATE rather than a ref, and the field waits for it: a ref is still
   * null on first paint, and this list OPENS on first paint, so it would portal
   * to `document.body` — outside the grid — and the very first click would
   * close the editor having written nothing. The extra render costs nothing.
   */
  const [panel, setPanel] = useState<HTMLDivElement | null>(null)

  //  Sorted by what the admin reads, not by whatever order the endpoint
  //  returned: fifty unsorted teams are a scroll, not a list.
  const options = Array.from(teamsById.values())
    .filter((team): team is TeamRef & { id: string } => Boolean(team.id))
    .map((team) => ({
      value: team.id,
      label: team.name_full ?? team.name ?? team.id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

  return (
    <div css={panelStyles} ref={setPanel}>
      {panel !== null && (
        <Select
          //  Explicit id: two team columns can be open in one session, and
          //  without it neither a test nor assistive tech can say which option
          //  list belongs to which field.
          id={TEAM_FIELD_ID}
          autoFocus
          //  Opened on mount, unlike the location field. That one stacks above
          //  a Surface field its list would cover; this panel holds one control
          //  and nothing to hide, so a double-click lands straight on the list.
          defaultOpen
          value={current}
          onChange={(next: string) => {
            onValueChange(next)
            //  Deferred a microtask: AG Grid unmounts the editor synchronously,
            //  and antd runs its own state updates right after `onChange`.
            queueMicrotask(() => stopEditing())
          }}
          options={options}
          loading={teamsLoading}
          showSearch
          optionFilterProp='label'
          placeholder={gameViewsLabel('gameGridTeamPlaceholder')}
          notFoundContent={
            teamsLoading
              ? gameViewsLabel('gameGridTeamsLoading')
              : gameViewsLabel('gameGridNoTeams')
          }
          style={selectStyle}
          getPopupContainer={() => panel}
        />
      )}
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

//  antd sizes its Select through `style`, not a class.
const selectStyle = { width: 240 }

const panelStyles = css`
  ${font.body}
  padding: 12px;
  background: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
`

export default TeamCellEditor
